import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Stack,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoIcon from '@mui/icons-material/Info';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getFileTypeIcon, formatFileSize, formatDocumentType } from '../../utils/documentUtils';
import {
  bucketCompanyDocumentsBySection,
  COMPANY_DOCUMENT_SECTIONS,
  CompanyDocumentSectionKey,
} from './companyDocumentSections';

export interface DocumentItem {
  id: string;
  title?: string;
  filename?: string;
  fileSize?: number;
  description?: string;
  createdAt?: string;
  documentDate?: string;
  expiryDate?: string;
  documentType?: string;
  reportingMonth?: string;
  fiscalYear?: number;
  documentVersion?: number;
  status?: string;
}

export interface FinancialStatementStatusView {
  periodType: 'MONTH' | 'FISCAL_YEAR';
  reportingMonth: string;
  fiscalYear: number;
  status?: 'UPLOADED' | 'MISSING' | 'LATE' | 'UNKNOWN';
  dueAt?: string;
  loading?: boolean;
}

interface CompanyDocumentsSectionProps {
  documents: DocumentItem[];
  onUploadClick: (defaultDocumentType: string) => void;
  onPreview: (doc: DocumentItem, index: number) => void;
  onEdit: (doc: DocumentItem) => void;
  onDownload: (docId: string, filename: string) => void;
  onDelete: (docId: string) => void;
  hasPermission: (permission: string) => boolean;
  financialStatementStatus?: FinancialStatementStatusView;
  onFinancialPeriodTypeChange?: (periodType: 'MONTH' | 'FISCAL_YEAR') => void;
  onFinancialReportingMonthChange?: (reportingMonth: string) => void;
  onFinancialFiscalYearChange?: (fiscalYear: number) => void;
}

const CompanyDocumentsSection: React.FC<CompanyDocumentsSectionProps> = ({
  documents,
  onUploadClick,
  onPreview,
  onEdit,
  onDownload,
  onDelete,
  hasPermission,
  financialStatementStatus,
  onFinancialPeriodTypeChange,
  onFinancialReportingMonthChange,
  onFinancialFiscalYearChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const grouped = bucketCompanyDocumentsBySection(documents);
  const sectionIcons: Record<CompanyDocumentSectionKey, React.ReactNode> = {
    financial: <AssessmentIcon fontSize="small" color="primary" />,
    other: <FolderIcon fontSize="small" color="primary" />,
    corporate: <AccountBalanceIcon fontSize="small" color="primary" />,
  };
  const statusChipConfig =
    financialStatementStatus?.status === 'UPLOADED'
      ? { label: 'Uploaded', color: 'success' as const, variant: 'filled' as const }
      : financialStatementStatus?.status === 'LATE'
        ? { label: 'Late', color: 'warning' as const, variant: 'filled' as const }
        : financialStatementStatus?.status === 'MISSING'
          ? { label: 'Missing', color: 'default' as const, variant: 'filled' as const }
          : { label: 'Unavailable', color: 'default' as const, variant: 'outlined' as const };
  const formatIsoDateUtc = (value?: string): string => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mb={2}
        >
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            Documents
            <Chip label={documents.length} size="small" color="primary" />
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1.5}>
          {COMPANY_DOCUMENT_SECTIONS.map((section) => {
            const sectionDocuments = grouped[section.key];
            return (
              <Accordion key={section.key} defaultExpanded={section.defaultExpanded} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    pr={1}
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {sectionIcons[section.key]}
                      <Typography variant="subtitle2" fontWeight="bold">
                        {section.title}
                      </Typography>
                    </Box>
                    <Chip label={sectionDocuments.length} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5 }}>
                  <Stack spacing={1}>
                    {section.key === 'financial' && financialStatementStatus && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          p: 1.25,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <Select
                          size="small"
                          value={financialStatementStatus.periodType}
                          onChange={(event) =>
                            onFinancialPeriodTypeChange?.(event.target.value as 'MONTH' | 'FISCAL_YEAR')
                          }
                          sx={{ minWidth: 140 }}
                          aria-label="Financial statement period type"
                        >
                          <MenuItem value="MONTH">Calendar Month</MenuItem>
                          <MenuItem value="FISCAL_YEAR">Fiscal Year</MenuItem>
                        </Select>
                        {financialStatementStatus.periodType === 'MONTH' ? (
                          <TextField
                            size="small"
                            type="month"
                            label="Month"
                            InputLabelProps={{ shrink: true }}
                            value={financialStatementStatus.reportingMonth}
                            onChange={(event) => onFinancialReportingMonthChange?.(event.target.value)}
                            sx={{ minWidth: 180 }}
                            inputProps={{ 'aria-label': 'Financial statement reporting month' }}
                          />
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            label="Fiscal Year"
                            value={financialStatementStatus.fiscalYear}
                            onChange={(event) => {
                              const nextFiscalYear = Number(event.target.value);
                              if (Number.isNaN(nextFiscalYear)) return;
                              onFinancialFiscalYearChange?.(nextFiscalYear);
                            }}
                            sx={{ minWidth: 160 }}
                            inputProps={{ 'aria-label': 'Financial statement fiscal year', min: 1900, max: 3000 }}
                          />
                        )}
                        <Chip
                          label={
                            financialStatementStatus.loading
                              ? 'Status: Checking...'
                              : `Status: ${statusChipConfig.label}`
                          }
                          color={financialStatementStatus.loading ? 'primary' : statusChipConfig.color}
                          variant={financialStatementStatus.loading ? 'outlined' : statusChipConfig.variant}
                        />
                        {financialStatementStatus.dueAt && (
                          <Typography variant="caption" color="text.secondary">
                            Due by {formatIsoDateUtc(financialStatementStatus.dueAt)}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {hasPermission('documents:create') && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        onClick={() => onUploadClick(section.defaultDocumentType)}
                        fullWidth={isMobile}
                        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                      >
                        {section.uploadButtonLabel}
                      </Button>
                    )}
                    {sectionDocuments.length === 0 ? (
                      <Alert severity="info">{section.emptyState}</Alert>
                    ) : (
                      <List dense disablePadding>
                        {sectionDocuments.map((doc, idx) => (
                          <React.Fragment key={doc.id}>
                            {idx > 0 && <Divider />}
                            <Box
                              display="flex"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              gap={1}
                              py={1.25}
                              flexDirection={{ xs: 'column', sm: 'row' }}
                            >
                              {getFileTypeIcon(doc.filename ?? '')}
                              <Box flex={1} minWidth={0} width="100%">
                                <Typography variant="body2" fontWeight="medium" sx={{ overflowWrap: 'anywhere' }}>
                                  {doc.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ overflowWrap: 'anywhere' }}
                                >
                                  {doc.filename} • {formatFileSize(doc.fileSize || 0)}
                                </Typography>
                                {doc.description && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ fontStyle: 'italic', overflowWrap: 'anywhere' }}
                                  >
                                    {doc.description}
                                  </Typography>
                                )}
                                <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                                  <Chip
                                    label={`Type: ${formatDocumentType(doc.documentType || 'OTHER')}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: '18px', fontSize: '0.7rem' }}
                                  />
                                  <Chip
                                    label={`Uploaded: ${doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : ''}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: '18px', fontSize: '0.7rem' }}
                                  />
                                  {doc.documentDate && (
                                    <Chip
                                      label={`Doc Date: ${formatIsoDateUtc(doc.documentDate)}`}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      sx={{ height: '18px', fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {doc.expiryDate && (
                                    <Chip
                                      label={`Expires: ${formatIsoDateUtc(doc.expiryDate)}`}
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      sx={{ height: '18px', fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {doc.reportingMonth && (
                                    <Chip
                                      label={`Month: ${doc.reportingMonth}`}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      sx={{ height: '18px', fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {doc.fiscalYear && (
                                    <Chip
                                      label={`FY: ${doc.fiscalYear}`}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      sx={{ height: '18px', fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {doc.documentVersion && (
                                    <Chip
                                      label={`v${doc.documentVersion}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: '18px', fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {doc.status === 'SUPERSEDED' && (
                                    <Chip
                                      label="Superseded"
                                      size="small"
                                      color="warning"
                                      sx={{ height: '18px', fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                  alignSelf: { xs: 'stretch', sm: 'center' },
                                  justifyContent: { xs: 'space-between', sm: 'flex-end' },
                                }}
                              >
                                <Tooltip title="Preview">
                                  <IconButton
                                    aria-label="Preview document"
                                    size="small"
                                    color="primary"
                                    onClick={() => onPreview(doc, documents.indexOf(doc))}
                                    sx={{ minWidth: 40, minHeight: 40 }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    aria-label="Edit document"
                                    size="small"
                                    color="default"
                                    onClick={() => onEdit(doc)}
                                    sx={{ minWidth: 40, minHeight: 40 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    aria-label="Download document"
                                    size="small"
                                    color="success"
                                    onClick={() => onDownload(doc.id, doc.filename ?? '')}
                                    sx={{ minWidth: 40, minHeight: 40 }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {hasPermission('documents:delete') && (
                                  <Tooltip title="Delete">
                                    <IconButton
                                      aria-label="Delete document"
                                      size="small"
                                      color="error"
                                      onClick={() => onDelete(doc.id)}
                                      sx={{ minWidth: 40, minHeight: 40 }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CompanyDocumentsSection;
